import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaSearch, FaDownload } from "react-icons/fa";
import { API_URL } from "../../../../api";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  Typography,
  Box,
  Button,
} from "@mui/material";

const PointHistories = () => {
  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: API_URL(),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const [histories, setHistories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getHistories = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("point-histories", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
        },
      });
      const list = res.data?.data || [];
      setHistories(list);
      setTotalCount(res.data?._metadata?.total || list.length);
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getHistories();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "earned":
        return "success";
      case "redeemed":
        return "primary";
      case "expired":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <div className="App">
      <div className="wrapper">
        <div className="content-header pb-2">
          <div className="container-fluid px-3">
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: "#333", display: "flex", alignItems: "center", gap: 1 }}
            >
              Point Histories
            </Typography>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid px-3">
            <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search by customer / shop..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0);
                    }}
                    sx={{ width: { xs: "100%", sm: "300px" } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaSearch color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FaDownload />}
                    sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                  >
                    Export
                  </Button>
                </Box>

                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: "1px solid #e0e0e0" }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow
                        sx={{
                          "& th": {
                            backgroundColor: "#f5f5f5",
                            color: "#333",
                            fontWeight: "bold",
                          },
                        }}
                      >
                        <TableCell>#</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Points</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Related Shop</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: "center", py: 5 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : histories.length > 0 ? (
                        histories.map((hist, i) => {
                          const customerName =
                            hist.customer?.name ||
                            hist.customer?.customerName ||
                            (typeof hist.customer === "string"
                              ? hist.customer
                              : null);
                          const shopName =
                            hist.relatedShop?.name ||
                            hist.relatedShop?.shopName ||
                            (typeof hist.relatedShop === "string"
                              ? hist.relatedShop
                              : null);

                          return (
                            <TableRow key={hist._id || i} hover>
                              <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                              <TableCell>{customerName || "-"}</TableCell>
                              <TableCell>{hist.points ?? hist.point ?? "-"}</TableCell>
                              <TableCell>{hist.type || "-"}</TableCell>
                              <TableCell>
                                {hist.status ? (
                                  <Chip
                                    label={hist.status}
                                    color={getStatusColor(hist.status)}
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>{shopName || "-"}</TableCell>
                              <TableCell
                                sx={{
                                  maxWidth: 200,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {hist.description || "-"}
                              </TableCell>
                              <TableCell>
                                {hist.createdAt
                                  ? new Date(hist.createdAt).toLocaleString()
                                  : "-"}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ textAlign: "center", py: 5, color: "text.secondary" }}
                          >
                            No point histories found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{ "& p": { marginBottom: 0 } }}
                    />
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PointHistories;